import React, { useState, type ChangeEvent, useEffect, useRef } from 'react';
import FileProgressSpinner from '../FileProgressSpinner';
import { appConfig } from '@/lib/AppConfig';

interface FilesSelectorProps {
  onImagesChange?: (formData: FormData | null) => void;
  multiple?: boolean;
  isUploading: boolean;
}

const FilesSelector: React.FC<FilesSelectorProps> = ({ onImagesChange, multiple = true, isUploading }) => {
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const validFiles = Array.from(files)
        .filter(file => file.type.startsWith('image/'));
      
      setErrorMessage('');
      const currentSize = imageFiles.reduce((sum, file) => sum + file.size, 0);
      const newFilesSize = validFiles.reduce((sum, file) => sum + file.size, 0);
      if (currentSize + newFilesSize > appConfig.maxFileUploadSize) {
        setErrorMessage('Adding these files would exceed the 200MB limit.');
        return;
      }

      const newImages = validFiles.map(file => URL.createObjectURL(file));
      setSelectedImages(prev => [...prev, ...newImages]);
      setImageFiles(prev => [...prev, ...validFiles]);
      
      const formData = new FormData();
      validFiles.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
      if (onImagesChange) {
        onImagesChange(validFiles.length > 0 ? formData : null);
      }
    }
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    const newFiles = imageFiles.filter((_, i) => i !== index);
    setImageFiles(newFiles);
    URL.revokeObjectURL(selectedImages[index]);

    // Update the input's files to reflect remaining files
    const dt = new DataTransfer();
    newFiles.forEach(file => dt.items.add(file));
    if (inputRef.current) {
      inputRef.current.files = dt.files;
    }

    // Create new FormData with remaining files
    const formData = new FormData();
    newFiles.forEach((file, i) => {
      formData.append(`file${i}`, file);
    });
    if (onImagesChange) {
      onImagesChange(newFiles.length > 0 ? formData : null);
    }
  };

  useEffect(() => {
    return () => {
      selectedImages.forEach(url => URL.revokeObjectURL(url));
    };
  }, [selectedImages]);

    return (
      isUploading ? (
        <FileProgressSpinner />
      ) : (
        <div className="max-w-3xl mx-auto md:max-w-none">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="select-images">
              Select Images
            </label>
            <input
              id="select-images"
              type="file"
              accept="image/*"
              multiple={multiple}
              onChange={handleImageChange}
              ref={inputRef}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                hover:file:bg-blue-100
                disabled:opacity-50"
            />
          </div>
          {errorMessage && (
            <p className="text-red-500 text-sm mb-4">{errorMessage}</p>
          )}
          {selectedImages.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {selectedImages.map((image, index) => (
                <div key={index} className="relative group">
                  <img
                    src={image}
                    alt={`Selected ${index + 1}`}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full
                      opacity-100 transition-opacity"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          {selectedImages.length === 0 && (
            <p className="text-gray-500 text-center">No images selected</p>
          )}
        </div>
      )
    );
};

export default FilesSelector;